/**
 * Route gate: platform admin OR chartMindI2Access grant.
 *
 * Unsigned visitors go to sign-in with a return path. Signed-in accounts
 * without a grant see an access-denied page (ChartMind-I2 has no marketing
 * waitlist counterpart to ClinicalHUD's request-access).
 */
import { Navigate, useLocation } from "react-router-dom";
import { Alert, Box, Typography } from "@mui/material";
import { LoadingButton } from "@mui/lab";
import Loading from "components/Loading";
import DataLoadErrorAlert from "components/common/DataLoadErrorAlert";
import { useAuth } from "hooks/useAuth";
import { useChartMindI2Access } from "../hooks/useChartMindI2Access";

export default function RequireChartMindI2Access({ children }) {
  const { user } = useAuth();
  const { canAccess, loading, accessState, errorCode, retry } = useChartMindI2Access();
  const location = useLocation();

  if (loading) return <Loading page />;
  if (canAccess) return children;
  if (!user) {
    const next = `${location.pathname}${location.search}`;
    return <Navigate to={`/auth?next=${encodeURIComponent(next)}`} replace />;
  }
  if (accessState === "error") {
    return (
      <Box sx={{ maxWidth: 560, mx: "auto", py: 6, px: 2 }}>
        <Typography variant="h5" sx={{ mb: 2 }}>
          ChartMind-I2
        </Typography>
        <DataLoadErrorAlert message="Couldn't confirm ChartMind-I2 access." />
        <LoadingButton onClick={retry}>Retry</LoadingButton>
        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
          {errorCode}
        </Typography>
      </Box>
    );
  }
  return (
    <Box sx={{ maxWidth: 560, mx: "auto", py: 6, px: 2 }}>
      <Typography variant="h5" sx={{ mb: 2 }}>
        ChartMind-I2
      </Typography>
      <Alert severity="warning">
        Your account does not have ChartMind-I2 access. Ask a platform admin to
        issue a <code>chartMindI2Access</code> grant.
      </Alert>
    </Box>
  );
}
